import { Image } from '@bfe/moly';
import { LoadingIcon } from '@bfe/moly-icons';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import {
  CSSProperties,
  ComponentProps,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

export type ImageWithDefaultProps = Omit<
  ComponentProps<typeof Image>,
  'src'
> & {
  //
  fallback?: ReactNode | (() => ReactNode);
  fallbackImage?: string;
  fallbackImageClassName?: string;
  placeholderClassName?: string;
  placeholderStyle?: CSSProperties;
  size?: number;
  src?: string;
};

export function ImageWithDefault({
  size,
  width = size,
  height = size,
  fallback,
  fallbackImage,
  fallbackImageClassName,
  className,
  placeholderClassName,
  placeholderStyle = {
    borderRadius: '50%',
    backgroundColor: ' var(--bds-gray-t4-dis)',
  },
  src,
  onError: _onError,
  onLoad,
  ...rest
}: ImageWithDefaultProps) {
  const [err, setError] = useState<unknown>();
  const errorImgUrl = useRef<string>();
  const [loading, setLoading] = useState<boolean>();
  useEffect(() => {
    // 更换图片的时候，如果有错误，就清除错误
    if (err) {
      setError(undefined);
    }
    if (!src) {
      return;
    }

    setLoading(true);
  }, [src]);

  const useFallback = !isNil(err) || !src;
  const imgUrl = useFallback
    ? errorImgUrl.current !== fallbackImage
      ? fallbackImage
      : undefined
    : src;

  // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
  const onError = (err: React.SyntheticEvent<HTMLImageElement>) => {
    errorImgUrl.current = imgUrl;
    setError(err);
    setLoading(false);
    _onError?.(err);
  };
  if (!imgUrl) {
    if (fallback) {
      return typeof fallback === 'function' ? fallback() : fallback;
    }
    return (
      <div
        className={clsx(className, placeholderClassName)}
        style={{
          width,
          height,
          verticalAlign: 'middle',
          textAlign: 'center',
          ...placeholderStyle,
        }}
      >
        {emptyImageIcon}
      </div>
    );
  }

  const handleImgUrl = (url: string) => {
    try {
      // eslint-disable-next-line jsx-control-statements/jsx-jcs-no-undef
      const urlObj = new URL(url);

      return urlObj.href;
    } catch {
      return url;
    }
  };

  return (
    <Image
      loading="lazy"
      {...rest}
      className={clsx(
        'transition-all',
        className,
        fallbackImage === imgUrl && fallbackImageClassName,
        loading ? 'opacity-0' : 'opacity-100'
      )}
      width={width}
      height={height}
      src={handleImgUrl(imgUrl)}
      onError={onError}
      onLoad={(e) => {
        setLoading(false);
        onLoad?.(e);
      }}
    />
  );
}

export function imageWithDefault(
  imgUrl: string | ReactNode | (() => ReactNode)
) {
  const addingProps =
    typeof imgUrl === 'string'
      ? { fallbackImage: imgUrl }
      : {
          fallback: imgUrl,
        };
  const c: React.FunctionComponent = (
    props: Omit<ImageWithDefaultProps, 'fallbackImage'>
  ) => {
    return <ImageWithDefault {...props} {...addingProps} />;
  };
  c.displayName = 'ImageWithDefault';
  return c;
}

const emptyImageIcon = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_11738_249531)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5391 10.53C9.55098 10.53 10.3713 9.68385 10.3713 8.64002C10.3713 7.59619 9.55098 6.75 8.5391 6.75C7.52723 6.75 6.70694 7.59619 6.70694 8.64002C6.70694 9.68385 7.52723 10.53 8.5391 10.53ZM7.92159 11.825C8.13297 11.4795 8.47556 11.4795 8.68752 11.825L10.2835 14.4289L13.5835 9.10913C13.7973 8.7643 14.1423 8.76294 14.3572 9.10913L19.0205 16.6269C19.2336 16.971 19.1107 17.25 18.7432 17.25H5.25924C4.8923 17.25 4.76584 16.9703 4.9778 16.6255L7.92159 11.825Z"
        fill="#ADB1B8"
      />
    </g>
    <defs>
      <clipPath id="clip0_11738_249531">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
